import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const includedFeatures = [
  'All 10+ Indian languages — Every language, every call, same ₹6/min. No regional surcharge, no dialect fees.',
  'Real-time dashboard — Live call logs, full transcripts, conversion tracking, and revenue recovered — all in one view.',
  'Instant webhook triggers — Plug into Shopify, WooCommerce, or any custom stack. Our API fires within seconds of cart drop.',
  'Custom voice scripts — Define your brand tone — warm, persuasive, concise. We train the agent to sound like your brand.',
  'No commitment. Ever. — Top up and use. Unused credits roll over. Pause, resume, or cancel at any point — no questions asked.',
  'Smart retry logic — Unanswered? The agent retries at the optimal time window, based on customer behaviour patterns.',
];

const AboutSection = () => {
  const openGetStarted = () => {
    window.open('https://forms.gle/rU28UfypBujmWmQK9', '_blank');
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
            Pricing
          </span>
          <h2
            data-testid="pricing-headline"
            className="mt-4 font-['DM_Sans'] text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1C1917] tracking-tight"
          >
            Pay only for what you use.
          </h2>
          <p className="mt-4 text-[#57534E] text-base md:text-lg max-w-xl mx-auto">
            No monthly fees. No per-seat charges. No hidden costs. Just ₹6 per minute of actual
            call time — billed to the minute.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-[#FAFAF9] rounded-3xl border border-[#E7E5E4] overflow-hidden"
        >
          <div className="bg-[#2563EB] px-8 py-6 text-center">
            <p className="text-white/80 text-sm font-medium mb-1 uppercase tracking-wider">
              Pay as you go — One plan. Every brand.
            </p>
            <p className="text-white/70 text-sm">
              Your rate stays the same whether you're running 10 calls a day or 10,000.
            </p>
          </div>

          <div className="px-8 py-10 text-center border-b border-[#E7E5E4]">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-6xl font-bold text-[#1C1917] font-['DM_Sans']">₹6</span>
              <div className="text-left">
                <div className="text-lg text-[#57534E] font-medium">/ per minute</div>
                <div className="text-sm text-[#78716C]">billed to the minute · no minimum</div>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            <p className="text-sm font-semibold text-[#1C1917] mb-5 uppercase tracking-wide">
              What's included:
            </p>
            <div className="space-y-4">
              {includedFeatures.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.07 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <span className="text-[#57534E] text-sm leading-relaxed">
                    <strong className="text-[#1C1917]">{feature.split(' — ')[0]}</strong>
                    {' — '}
                    {feature.split(' — ').slice(1).join(' — ')}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="px-8 pb-8 pt-2 border-t border-[#E7E5E4] bg-[#EFF6FF] text-center">
            <p className="text-[#1C1917] font-semibold text-lg mb-1">
              Start with 100 free calls.
            </p>
            <p className="text-[#57534E] text-sm mb-6">
              No card required. No commitment. Sign up, connect your store, and watch the
              first recoveries come in — completely free.
            </p>
            <Button
              data-testid="pricing-cta"
              onClick={openGetStarted}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-5 text-base font-semibold btn-shine group"
            >
              Claim your 100 free calls
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 text-center text-sm text-[#78716C]"
        >
          Average cart recovery call: 2–3 minutes → ₹12–₹18 per attempt. Recover one ₹500
          order and you've already paid for 25+ calls.{' '}
          <a
            href="mailto:contact@neurobloomai.com"
            className="text-[#2563EB] hover:underline"
          >
            Volume pricing available for 10,000+ min/month — reach out.
          </a>
        </motion.p>
      </div>
    </section>
  );
};

export default AboutSection;
